import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { util as sdkUtil } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { linkPreviewAPI, communityAPI } from '../../util/api';
import { fetchCommunitySidebarData } from '../../util/communityTeam';
import { maybeOpenNewsletterPrompt } from '../../ducks/newsletterPrompt.duck';
import config from '../../config';

const RESULT_PAGE_SIZE = 10;
const FEATURED_POSTS_PAGE_SIZE = 5;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/CommunityPage/SET_INITIAL_STATE';

export const SEARCH_POSTS_REQUEST = 'app/CommunityPage/SEARCH_POSTS_REQUEST';
export const SEARCH_POSTS_SUCCESS = 'app/CommunityPage/SEARCH_POSTS_SUCCESS';
export const SEARCH_POSTS_ERROR = 'app/CommunityPage/SEARCH_POSTS_ERROR';

export const FETCH_FEATURED_POSTS_REQUEST = 'app/CommunityPage/FETCH_FEATURED_POSTS_REQUEST';
export const FETCH_FEATURED_POSTS_SUCCESS = 'app/CommunityPage/FETCH_FEATURED_POSTS_SUCCESS';
export const FETCH_FEATURED_POSTS_ERROR = 'app/CommunityPage/FETCH_FEATURED_POSTS_ERROR';

export const GET_LINK_PREVIEWS_REQUEST = 'app/CommunityPage/GET_LINK_PREVIEWS_REQUEST';
export const GET_LINK_PREVIEWS_SUCCESS = 'app/CommunityPage/GET_LINK_PREVIEWS_SUCCESS';
export const GET_LINK_PREVIEWS_ERROR = 'app/CommunityPage/GET_LINK_PREVIEWS_ERROR';

export const ADD_POST_COUNT_REQUEST = 'app/CommunityPage/ADD_POST_COUNT_REQUEST';
export const ADD_POST_COUNT_SUCCESS = 'app/CommunityPage/ADD_POST_COUNT_SUCCESS';
export const ADD_POST_COUNT_ERROR = 'app/CommunityPage/ADD_POST_COUNT_ERROR';

export const FETCH_SIDEBAR_REQUEST = 'app/CommunityPage/FETCH_SIDEBAR_REQUEST';
export const FETCH_SIDEBAR_SUCCESS = 'app/CommunityPage/FETCH_SIDEBAR_SUCCESS';
export const FETCH_SIDEBAR_ERROR = 'app/CommunityPage/FETCH_SIDEBAR_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchPostsInProgress: false,
  searchPostsError: null,
  currentPageResultIds: [],
  fetchFeaturedPostsInProgress: false,
  fetchFeaturedPostsError: null,
  featuredPostsResultIds: [],
  getLinkPreviewsInProgress: false,
  getLinkPreviewsError: null,
  linkPreviews: [],
  addPostCountInProgress: false,
  addPostCountError: null,
  sidebarTeam: [],
  sidebarMemberCount: 0,
  fetchSidebarInProgress: false,
  fetchSidebarError: null,
};

const resultIds = data => data.data.map(l => l.id);

export default function communityPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };

    case SEARCH_POSTS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchPostsInProgress: true,
        searchPostsError: null,
      };
    case SEARCH_POSTS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchPostsInProgress: false,
      };
    case SEARCH_POSTS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchPostsError: payload };

    case FETCH_FEATURED_POSTS_REQUEST:
      return {
        ...state,
        fetchFeaturedPostsInProgress: true,
        fetchFeaturedPostsError: null,
      };
    case FETCH_FEATURED_POSTS_SUCCESS:
      return {
        ...state,
        featuredPostsResultIds: resultIds(payload.data),
        fetchFeaturedPostsInProgress: false,
      };
    case FETCH_FEATURED_POSTS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, fetchFeaturedPostsInProgress: false, fetchFeaturedPostsError: payload };

    case GET_LINK_PREVIEWS_REQUEST:
      return {
        ...state,
        getLinkPreviewsInProgress: true,
        getLinkPreviewsError: null,
      };
    case GET_LINK_PREVIEWS_SUCCESS:
      return {
        ...state,
        linkPreviews: payload.linkPreview,
        getLinkPreviewsInProgress: false,
      };
    case GET_LINK_PREVIEWS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, getLinkPreviewsInProgress: false, getLinkPreviewsError: payload };

    case ADD_POST_COUNT_REQUEST:
      return { ...state, addPostCountInProgress: true, addPostCountError: null };
    case ADD_POST_COUNT_SUCCESS:
      return { ...state, addPostCountInProgress: false };
    case ADD_POST_COUNT_ERROR:
      return { ...state, addPostCountInProgress: false, addPostCountError: payload };

    case FETCH_SIDEBAR_REQUEST:
      return { ...state, fetchSidebarInProgress: true, fetchSidebarError: null };
    case FETCH_SIDEBAR_SUCCESS:
      return {
        ...state,
        fetchSidebarInProgress: false,
        sidebarTeam: payload?.team || [],
        sidebarMemberCount: payload?.memberCount || 0,
      };
    case FETCH_SIDEBAR_ERROR:
      return { ...state, fetchSidebarInProgress: false, fetchSidebarError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const searchPostsRequest = searchParams => ({
  type: SEARCH_POSTS_REQUEST,
  payload: { searchParams },
});
export const searchPostsSuccess = response => ({
  type: SEARCH_POSTS_SUCCESS,
  payload: { data: response.data },
});
export const searchPostsError = e => ({
  type: SEARCH_POSTS_ERROR,
  error: true,
  payload: e,
});

export const fetchFeaturedPostsRequest = () => ({
  type: FETCH_FEATURED_POSTS_REQUEST,
});
export const fetchFeaturedPostsSuccess = response => ({
  type: FETCH_FEATURED_POSTS_SUCCESS,
  payload: { data: response.data },
});
export const fetchFeaturedPostsError = e => ({
  type: FETCH_FEATURED_POSTS_ERROR,
  error: true,
  payload: e,
});

export const getLinkPreviewsRequest = () => ({
  type: GET_LINK_PREVIEWS_REQUEST,
});
export const getLinkPreviewsSuccess = linkPreview => ({
  type: GET_LINK_PREVIEWS_SUCCESS,
  payload: { linkPreview },
});
export const getLinkPreviewsError = e => ({
  type: GET_LINK_PREVIEWS_ERROR,
  error: true,
  payload: e,
});

export const addPostCountRequest = () => ({
  type: ADD_POST_COUNT_REQUEST,
});
export const addPostCountSuccess = () => ({
  type: ADD_POST_COUNT_SUCCESS,
});
export const addPostCountError = e => ({
  type: ADD_POST_COUNT_ERROR,
  error: true,
  payload: e,
});

export const fetchSidebarRequest = () => ({ type: FETCH_SIDEBAR_REQUEST });
export const fetchSidebarSuccess = ({ team, memberCount }) => ({
  type: FETCH_SIDEBAR_SUCCESS,
  payload: { team, memberCount },
});
export const fetchSidebarError = e => ({
  type: FETCH_SIDEBAR_ERROR,
  error: true,
  payload: e,
});

// Resolves the 6 team emails + total member count via the existing
// /api/users/query endpoint. Errors are swallowed into an empty
// state so the rest of the page still renders if the bundle fails.
export const fetchCommunitySidebar = () => async dispatch => {
  dispatch(fetchSidebarRequest());
  try {
    const data = await fetchCommunitySidebarData();
    dispatch(fetchSidebarSuccess(data));
  } catch (e) {
    dispatch(fetchSidebarError(storableError(e)));
  }
};

// ================ Thunks ================ //

export const searchPosts = searchParams => (dispatch, getState, sdk) => {
  dispatch(searchPostsRequest(searchParams));

  const { perPage, sort, ...rest } = searchParams;

  const params = {
    ...rest,
    per_page: perPage,
    sort: sort || 'meta_voteCount',
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(searchPostsSuccess(response));

      const posts = response.data.data;
      const linkPosts = posts.filter(
        p => p.attributes.publicData.contentType === 'link' && p.attributes.publicData.linkUrl
      );
      dispatch(getLinkPreviews(linkPosts));

      return response;
    })
    .catch(e => {
      dispatch(searchPostsError(storableError(e)));
      throw e;
    });
};

export const fetchFeaturedPosts = () => (dispatch, getState, sdk) => {
  dispatch(fetchFeaturedPostsRequest());

  const params = {
    per_page: FEATURED_POSTS_PAGE_SIZE,
    pub_type: config.listingTypes['community'],
    meta_isFeatured: true,
    sort: 'meta_voteCount',
    include: ['author', 'author.profileImage'],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'profile.metadata',
    ],
    'fields.image': ['variants.square-small', 'variants.square-small2x', 'variants.default'],
    'limit.images': 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchFeaturedPostsSuccess(response));

      return response;
    })
    .catch(e => {
      dispatch(fetchFeaturedPostsError(storableError(e)));
      throw e;
    });
};

export const getLinkPreviews = posts => async (dispatch, getState, sdk) => {
  dispatch(getLinkPreviewsRequest());

  try {
    const linkPreviewResults = await Promise.all(
      posts.map(async post => {
        const linkUrl = post.attributes.publicData.linkUrl;
        const response = await linkPreviewAPI.getLinkPreview({ url: linkUrl });
        return {
          id: post.id,
          linkPreview: response.data,
        };
      })
    );

    dispatch(getLinkPreviewsSuccess(linkPreviewResults));
    return linkPreviewResults;
  } catch (error) {
    dispatch(getLinkPreviewsError(storableError(error)));
    throw error;
  }
};

export const addPostCount = params => (dispatch, getState, sdk) => {
  dispatch(addPostCountRequest(params.action));

  return communityAPI.posts
    .addCount(params)
    .then(response => {
      dispatch(addPostCountSuccess());
      return response;
    })
    .catch(e => {
      dispatch(addPostCountError(storableError(e)));
    });
};

export const loadData = (params, search) => dispatch => {
  const queryParams = parse(search);
  const { page = 1, address, origin, ...rest } = queryParams;

  const searchPostsCall = searchPosts({
    ...rest,
    pub_type: config.listingTypes['community'],
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images', 'author', 'author.profileImage'],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'profile.metadata',
    ],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.default',

      // custom variants
      'variants.portrait-crop2x',
    ],
    'imageVariant.portrait-crop2x': sdkUtil.objectQueryString({
      w: 560,
      h: 784,
      fit: 'crop',
    }),
    'limit.images': 3,
  });

  dispatch(loadClientData());

  return Promise.all([
    dispatch(searchPostsCall),
    dispatch(fetchFeaturedPosts()),
    dispatch(fetchCommunitySidebar()),
  ]);
};

// The visitor's own part of the page, loaded in the browser (see onlyClientDataNeeded in
// src/Routes.js): the newsletter prompt, gated by its own should-show check (it does nothing
// on the server), so the page render doesn't wait on it.
export const loadClientData = () => dispatch => dispatch(maybeOpenNewsletterPrompt());
